import type { ModalPreferences } from "../Types/ModalPreferences";
import {
  API_BASE_URL,
  GET_USER_MODAL_PREFERENCES_API,
  ADD_USER_SETTINGS_API,
  SAVE_USER_MODAL_PREFERENCES_API,
} from "../Utils/Config";
import { getRequest, postRequest, putRequest } from "./AxiosConfiguration";

export const LONG_TERM_SCENARIO = "longTermScenario";

const DEFAULT_LONG_TERM_PREFERENCES: ModalPreferences = {
  selection: "create",
  isProcessingPackagingPlantsDiff: true,
  isSourceBePartOfOptimization: true,
  isPackingPlantAddMinorIngredient: true,
  plantClosure: true,
  processingPlantsCount: 0,
  packagingPlantsCount: 0,
  lastModuleKey: "module1",
};

function buildDefaultSettingsPayload(userId: string | number) {
  return {
    userId: String(userId),
    settings: {
      shortTerm: {
        selection: "create",
        homeCare: true,
        nutrition: true,
      },
      mediumTerm: {
        selection: "create",
        homeCare: true,
        nutrition: true,
      },
      longTerm: {
        selection: DEFAULT_LONG_TERM_PREFERENCES.selection,
        isProcessingPackagingPlantsDiff:
          DEFAULT_LONG_TERM_PREFERENCES.isProcessingPackagingPlantsDiff,
        isSourceBePartOfOptimization:
          DEFAULT_LONG_TERM_PREFERENCES.isSourceBePartOfOptimization,
        isPackingPlantAddMinorIngredient:
          DEFAULT_LONG_TERM_PREFERENCES.isPackingPlantAddMinorIngredient,
        folderName: "",
        scenarioName: "",
        plantClosure: DEFAULT_LONG_TERM_PREFERENCES.plantClosure,
        processingPlantsCount:
          DEFAULT_LONG_TERM_PREFERENCES.processingPlantsCount,
        packagingPlantsCount:
          DEFAULT_LONG_TERM_PREFERENCES.packagingPlantsCount,
        lastModuleKey: DEFAULT_LONG_TERM_PREFERENCES.lastModuleKey ?? "module1",
      },
    },
  };
}

function mapLongTermToPreferences(longTerm: any): ModalPreferences {
  const selection =
    longTerm.selection === "view" || longTerm.viewExistingScenario === true
      ? "view"
      : "create";
  return {
    selection,
    isProcessingPackagingPlantsDiff:
      longTerm.isProcessingPackagingPlantsDiff ?? true,
    isSourceBePartOfOptimization: longTerm.isSourceBePartOfOptimization ?? true,
    isPackingPlantAddMinorIngredient:
      longTerm.isPackingPlantAddMinorIngredient ?? true,
    plantClosure: longTerm.plantClosure ?? false,
    processingPlantsCount: longTerm.processingPlantsCount ?? 0,
    packagingPlantsCount: longTerm.packagingPlantsCount ?? 0,
    selectedFolderId: longTerm.selectedFolderId ?? longTerm.folderId,
    folderName: longTerm.folderName ?? null,
    scenarioName: longTerm.scenarioName ?? null,
    lastModuleKey: longTerm.lastModuleKey,
  } as ModalPreferences;
}

/**
 * Fetches user modal preferences from backend.
 * When all sections are null, creates default settings via addsetting API and returns defaults.
 */
export const getUserModalPreferences = async (
  modalKey: string,
  userId: string | number | undefined,
): Promise<ModalPreferences | null> => {
  if (!userId) return null;

  const urls = API_BASE_URL + GET_USER_MODAL_PREFERENCES_API;
  const header = {
    accept: "application/json",
    "Content-Type": "application/json",
  };

  try {
    const response = await getRequest(urls, { userId }, header);
    const data = response?.data ?? {};
    const { shortTerm, mediumTerm, longTerm } = data;

    if (shortTerm === null && mediumTerm === null && longTerm === null) {
      const addSettingUrl = API_BASE_URL + ADD_USER_SETTINGS_API;
      const payload = buildDefaultSettingsPayload(userId);
      try {
        await postRequest(addSettingUrl, payload, header);
      } catch (err: any) {
        const errData = err?.response?.data;
        const errStatus = err?.response?.status;
        const errMsg = err?.message;
        console.error("[ModalPreferenceService] addsetting failed:", {
          url: err?.config?.url,
          method: err?.config?.method,
          status: errStatus,
          statusText: err?.response?.statusText,
          data: errData,
          message: errMsg,
        });
        try {
          const payloadPascal = {
            UserId: String(userId),
            Settings: {
              ShortTerm: {
                Selection: "create",
                HomeCare: true,
                Nutrition: true,
              },
              MediumTerm: {
                Selection: "create",
                HomeCare: true,
                Nutrition: true,
              },
              LongTerm: {
                Selection: payload.settings.longTerm.selection,
                IsProcessingPackagingPlantsDiff:
                  payload.settings.longTerm.isProcessingPackagingPlantsDiff,
                IsSourceBePartOfOptimization:
                  payload.settings.longTerm.isSourceBePartOfOptimization,
                IsPackingPlantAddMinorIngredient:
                  payload.settings.longTerm.isPackingPlantAddMinorIngredient,
                FolderName: "",
                ScenarioName: "",
                PlantClosure: payload.settings.longTerm.plantClosure,
                ProcessingPlantsCount:
                  payload.settings.longTerm.processingPlantsCount,
                PackagingPlantsCount:
                  payload.settings.longTerm.packagingPlantsCount,
                LastModuleKey: payload.settings.longTerm.lastModuleKey,
              },
            },
          };
          await postRequest(addSettingUrl, payloadPascal, header);
        } catch (err2: any) {
          console.error(
            "[ModalPreferenceService] addsetting PascalCase fallback failed:",
            err2?.response?.data ?? err2?.message,
          );
        }
      }
      if (modalKey === LONG_TERM_SCENARIO) {
        return DEFAULT_LONG_TERM_PREFERENCES;
      }
      return null;
    }

    if (modalKey === LONG_TERM_SCENARIO && longTerm) {
      return mapLongTermToPreferences(longTerm);
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Saves user modal preferences to backend (updatesetting API).
 * Uses same payload structure as ADD_USER_SETTINGS_API.
 */
export const saveUserModalPreferences = async (
  modalKey: string,
  userId: string | number | undefined,
  preferences: ModalPreferences,
): Promise<void> => {
  if (!userId) return;

  const header = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const urls = API_BASE_URL + SAVE_USER_MODAL_PREFERENCES_API;
  const payload = {
    userId: String(userId),
    settings: {
      shortTerm: {
        selection: "create",
        homeCare: true,
        nutrition: true,
      },
      mediumTerm: {
        selection: "create",
        homeCare: true,
        nutrition: true,
      },
      longTerm: {
        selection: preferences.selection,
        isProcessingPackagingPlantsDiff:
          preferences.isProcessingPackagingPlantsDiff,
        isSourceBePartOfOptimization: preferences.isSourceBePartOfOptimization,
        isPackingPlantAddMinorIngredient:
          preferences.isPackingPlantAddMinorIngredient,
        folderName: preferences.folderName ?? "",
        scenarioName: preferences.scenarioName ?? "",
        plantClosure: preferences.plantClosure,
        processingPlantsCount: preferences.processingPlantsCount,
        packagingPlantsCount: preferences.packagingPlantsCount,
        lastModuleKey: preferences.lastModuleKey ?? "module1",
      },
    },
  };
  try {
    await postRequest(urls, payload, header);
  } catch (err: any) {
    const status = err?.response?.status;
    const statusText = err?.response?.statusText;
    const data = err?.response?.data;
    const reqUrl = err?.config?.url;
    const method = err?.config?.method;
    console.error("[ModalPreferenceService] updatesetting (POST) failed:", {
      url: reqUrl,
      method,
      status,
      statusText,
      data,
      message: err?.message,
    });
    if (status === 404 || status === 405) {
      try {
        await putRequest(urls, payload, header);
      } catch (err2: any) {
        console.error(
          "[ModalPreferenceService] updatesetting (PUT fallback) failed:",
          err2?.response?.status,
          err2?.response?.data ?? err2?.message,
        );
      }
    }
  }
};
